// src/hooks/useSubdeckProgress.ts
// =====================================================================
// HOOK: Dados de progresso longitudinal do subdeck
// Persiste entre sessões via localStorage
// =====================================================================

import { useState, useCallback } from 'react';

export interface LastSessionData {
    accuracy: number;
    cardsStudied: number;
    timestamp: number; // epoch ms
    masteryPercentage: number;
}

export interface SubdeckProgressData {
    // Domínio
    masteryPercentage: number;
    masteryDelta: number; // diferença desde a última sessão
    // Cobertura
    uniqueCardsStudied: number;
    totalCards: number;
    // Distribuição acumulada
    statusDistribution: {
        new: number;
        learning: number;
        reviewing: number;
        mastered: number;
    };
    globalAccuracy: number;
    // Sessão anterior
    lastSession: LastSessionData | null;
    // Comparação
    accuracyDelta: number | null;
}

const STORAGE_PREFIX = 'graia_subdeck_session_';

function getStorageKey(deckId: string, subdeckId: string) {
    return `${STORAGE_PREFIX}${deckId}_${subdeckId}`;
}

export function useSubdeckProgress(
    deckId: string,
    subdeckId: string,
    isCustomDeck: boolean,
    displayedMastery: number,
    sessionAccuracy: number,
    sessionCardsStudied: number
) {
    const [data, setData] = useState<SubdeckProgressData | null>(null);
    const [loading, setLoading] = useState(false);

    // Carregar última sessão salva
    const loadLastSession = useCallback((): LastSessionData | null => {
        try {
            const key = getStorageKey(deckId, subdeckId);
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }, [deckId, subdeckId]);

    // Salvar sessão atual (chamado ao fechar/sair)
    const saveCurrentSession = useCallback(() => {
        if (!deckId || !subdeckId || sessionCardsStudied === 0) return;
        try {
            const key = getStorageKey(deckId, subdeckId);
            const sessionData: LastSessionData = {
                accuracy: sessionAccuracy,
                cardsStudied: sessionCardsStudied,
                timestamp: Date.now(),
                masteryPercentage: Math.round(displayedMastery),
            };
            localStorage.setItem(key, JSON.stringify(sessionData));
        } catch {
            // silencioso
        }
    }, [deckId, subdeckId, sessionAccuracy, sessionCardsStudied, displayedMastery]);

    // Buscar dados do servidor
    const fetchProgressData = useCallback(async () => {
        if (!deckId || !subdeckId) return;
        setLoading(true);

        try {
            const token = localStorage.getItem('accessToken');
            const statsUrl = isCustomDeck
                ? `/api/progress/custom-stats/${deckId}/${subdeckId}`
                : `/api/progress/stats/${deckId}/${subdeckId}`;

            const response = await fetch(statsUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) return;

            const stats = await response.json();
            const lastSession = loadLastSession();

            // NOVO: usar valor real do servidor como fonte primária de domínio
            // O displayedMastery local pode estar divergindo durante a sessão
            // O servidor já calcula corretamente: totalMastery / totalCards
            const serverMastery = Math.round(stats.masteryPercentage);

            // Para o delta: comparar com a última sessão salva
            // Se não há sessão anterior, delta = 0
            const masteryDelta = lastSession
                ? serverMastery - lastSession.masteryPercentage
                : 0;

            // Cards únicos = cards que têm qualquer progresso registrado
            // O backend retorna totalCards e statusDistribution (new = sem progresso ainda)
            const uniqueStudied = stats.totalCards - (stats.statusDistribution?.new || 0);

            // Comparação de precisão
            const accuracyDelta = lastSession
                ? stats.accuracy - lastSession.accuracy
                : null;

            setData({
                masteryPercentage: serverMastery,  // NOVO: usa valor do servidor, não local
                masteryDelta,
                uniqueCardsStudied: uniqueStudied,
                totalCards: stats.totalCards,
                statusDistribution: stats.statusDistribution || {
                    new: 0, learning: 0, reviewing: 0, mastered: 0
                },
                globalAccuracy: stats.accuracy,
                lastSession,
                accuracyDelta,
            });
        } catch (error) {
            console.error('❌ [useSubdeckProgress] Erro:', error);
        } finally {
            setLoading(false);
        }
    }, [deckId, subdeckId, isCustomDeck, loadLastSession]);

    return {
        data,
        loading,
        fetchProgressData,
        saveCurrentSession,
    };
}